import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import LudoBoard from "./LudoBoard";
import { createRealtimeClient } from "./realtime";
import { initSoundEffects, playSound } from "./soundEffects";
import { useAuth } from "./context/AuthContext";
import LeaderboardModal from "./components/LeaderboardModal";
import LudoManiaHero from "./components/LudoManiaHero";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const NEARBY_PLAYER_COUNT = 4;

function makeRoomCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function getClientId() {
  return Math.random().toString(36).slice(2, 10);
}

const App = () => {
  const [playerCount, setPlayerCount] = useState(4);
  const [svgSize] = useState(800);
  const [roomCode, setRoomCode] = useState("");
  const [playerName, setPlayerName] = useState("Player");
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [room, setRoom] = useState(null);
  const [localPlayerId, setLocalPlayerId] = useState(null);
  const [robotMatch, setRobotMatch] = useState(false);
  const [speedMode, setSpeedMode] = useState(false);
  const [teamMode, setTeamMode] = useState(false);
  const [gameKey, setGameKey] = useState(0);
  const [remoteGameState, setRemoteGameState] = useState(null);
  const [remoteAction, setRemoteAction] = useState(null);
  const [turnInfo, setTurnInfo] = useState({ currentPlayer: 0, colors: [] });
  const [error, setError] = useState("");
  const [roomModalMode, setRoomModalMode] = useState(null);
  const [authModalMode, setAuthModalMode] = useState(null);
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [playerPoints, setPlayerPoints] = useState({});
  const [matchType, setMatchType] = useState("4");   // "team"|"4"|"6"|"8"
  const [matchMode, setMatchMode] = useState("normal"); // "normal"|"speed"
  const [spectatorMode, setSpectatorMode] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [spectatorReactions, setSpectatorReactions] = useState([]);
  const [isRoomPublic, setIsRoomPublic] = useState(false);
  const [publicRooms, setPublicRooms] = useState([]);
  const [loadingPublicRooms, setLoadingPublicRooms] = useState(false);
  const [autoTurnPlayers, setAutoTurnPlayers] = useState({});
  // "nearby" state: 'idle' | 'searching' | 'hosting' | 'joining'
  const [nearbyStatus, setNearbyStatus] = useState("idle");
  const [nearbyRoomCode, setNearbyRoomCode] = useState("");
  const gameResultSavedRef = useRef(false);
  const gameVersionRef = useRef(0); // mirrors server's gameVersion; stamps every sync_state

  const { user, session, signInWithEmail, signUp, signInWithOAuth, signOut, loading: sessionLoading } = useAuth();
  const clientRef = useRef(null);
  const didReportConnectionStatus = useRef(false);

  const clientId = useMemo(getClientId, []);

  // Pre-fill room code from ?room=XXXX invite link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteCode = params.get("room");
    if (inviteCode) {
      setRoomCode(inviteCode.toUpperCase());
      setRoomModalMode("join");
    }
  }, []);

  useEffect(() => {
    initSoundEffects();
  }, []);

  useEffect(() => {
    clientRef.current = createRealtimeClient(
      ({ type, payload }) => {
        if (type === "joined_room") {
          playSound("room");
          setError("");
          setRobotMatch(false);
          setRoom(payload);
          setLocalPlayerId(payload.playerId);
          setPlayerCount(payload.playerCount || 4);
          setRemoteGameState(payload.gameState || null);
          // Align local version counter with the server's authoritative version
          gameVersionRef.current = payload.gameVersion ?? 0;
          setRoomModalMode(null);
          setNearbyStatus("idle");
          setNearbyRoomCode("");
          return;
        }

        if (type === "room_update") {
          playSound("status");
          setRoom(payload);
          if (payload.playerCount) setPlayerCount(payload.playerCount);
          return;
        }

        if (type === "state_synced") {
          if (payload.playerCount) setPlayerCount(payload.playerCount);
          if (payload.version !== undefined) {
            // Skip re-render when this is our own sync echoed back (already up-to-date)
            if (payload.version === gameVersionRef.current) return;
            gameVersionRef.current = payload.version;
          }
          setRemoteGameState(payload.gameState || null);
          return;
        }

        if (type === "sync_ack") {
          // Server confirmed our sync — update version without re-rendering
          if (payload.version !== undefined) gameVersionRef.current = payload.version;
          return;
        }

        if (type === "sync_rejected") {
          // Our sync was out-of-order — roll back to the server's authoritative state
          gameVersionRef.current = payload.serverVersion ?? gameVersionRef.current;
          if (payload.playerCount) setPlayerCount(payload.playerCount);
          setRemoteGameState(payload.gameState || null);
          return;
        }

        if (type === "dice_rolled") {
          setRemoteAction({
            id: `${Date.now()}-${Math.random()}`,
            type: "roll",
            playerId: payload.playerId,
            dice: payload.dice,
          });
          return;
        }

        if (type === "goti_moved") {
          setRemoteAction({
            id: `${Date.now()}-${Math.random()}`,
            type: "move",
            playerId: payload.playerId,
            gotiId: payload.gotiId,
          });
          return;
        }

        if (type === "spectate_joined") {
          setSpectatorMode(true);
          setSpectatorCount(payload.spectatorCount || 0);
          setPlayerCount(payload.playerCount || 4);
          setLocalPlayerId(null);
          setRemoteGameState(payload.gameState || null);
          setRoom({ roomId: payload.roomId, players: payload.players || [], playerCount: payload.playerCount || 4 });
          setRoomModalMode(null);
          return;
        }

        if (type === "spectator_count") {
          setSpectatorCount(payload.count || 0);
          return;
        }

        if (type === "spectator_reaction_broadcast") {
          const id = Date.now() + Math.random();
          const x = 8 + Math.random() * 84;
          setSpectatorReactions((prev) => [...prev, { id, emoji: payload.emoji, x }]);
          setTimeout(() => setSpectatorReactions((prev) => prev.filter((r) => r.id !== id)), 2200);
          return;
        }

        if (type === "room_visibility_changed") {
          setIsRoomPublic(Boolean(payload.isPublic));
          return;
        }

        if (type === "authenticated") {
          // Server verified the JWT — name is now locked to the Supabase profile
          return;
        }

        if (type === "auth_error") {
          console.warn("WebSocket auth failed:", payload.message);
          return;
        }

        if (type === "error") {
          playSound("blocked");
          setError(payload.message || "Realtime error.");
        }
      },
      setConnectionStatus,
    );

    return () => clientRef.current?.close();
  }, []);

  useEffect(() => {
    if (!didReportConnectionStatus.current) {
      didReportConnectionStatus.current = true;
      return;
    }

    if (connectionStatus === "connected") {
      playSound("status");
    }

    if (connectionStatus === "disconnected" || connectionStatus === "error") {
      playSound("blocked", { volume: 0.12 });
    }
  }, [connectionStatus]);

  function joinRoom(nextRoomCode = roomCode) {
    const cleanRoomCode = nextRoomCode.trim().toUpperCase();
    if (!cleanRoomCode) {
      setError("Enter a room code.");
      return;
    }

    setError("");
    setRoomCode(cleanRoomCode);
    clientRef.current?.send("join_room", {
      roomId: cleanRoomCode,
      clientId,
      name: playerName.trim() || "Player",
      playerCount,
    });
  }

  function deriveMatchSettings() {
    const isTeam  = matchType === "team";
    const isSpeed = matchMode === "speed";
    const pc      = isTeam ? 4 : Number(matchType);
    return { isTeam, isSpeed, pc };
  }

  function handleStartAI() {
    const { isTeam, isSpeed, pc } = deriveMatchSettings();
    setPlayerCount(pc);
    setTeamMode(isTeam);
    setSpeedMode(isSpeed);
    playSound("room");
    setError("");
    setRoom(null);
    setRobotMatch(true);
    setLocalPlayerId(0);
    setRemoteGameState(null);
    setRemoteAction(null);
    setGameKey((key) => key + 1);
    setRoomModalMode(null);
    gameResultSavedRef.current = false;
  }

  function handleCreateRoom() {
    const { isTeam, isSpeed, pc } = deriveMatchSettings();
    setPlayerCount(pc);
    setTeamMode(isTeam);
    setSpeedMode(isSpeed);
    const nextRoomCode = makeRoomCode();
    setRoomCode(nextRoomCode);
    setError("");
    clientRef.current?.send("join_room", {
      roomId: nextRoomCode,
      clientId,
      name: playerName.trim() || "Player",
      playerCount: pc,
    });
    setRoomModalMode(null);
  }

  function startRobotMatchWithCount(count) {
    setPlayerCount(count);
    setTeamMode(false);
    setSpeedMode(false);
    playSound("room");
    setError("");
    setRoom(null);
    setRobotMatch(true);
    setLocalPlayerId(0);
    setRemoteGameState(null);
    setRemoteAction(null);
    setGameKey((key) => key + 1);
    gameResultSavedRef.current = false;
  }

  function openRoomModal(mode) {
    setError("");
    setNavOpen(false);
    setRoomModalMode(mode);
  }

  useEffect(() => {
    if (user?.name) setPlayerName(user.name);
  }, [user]);

  // Authenticate WebSocket whenever session changes (login, token refresh, logout)
  useEffect(() => {
    if (session?.access_token) {
      clientRef.current?.authenticate(session.access_token);
    }
  }, [session]);

  // Upsert player profile in DB whenever user logs in
  useEffect(() => {
    if (!user || !session) return;
    fetch(`${API_URL}/api/profile/upsert`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).catch(() => {});
  }, [user, session]);

  async function saveGameResult(gameState) {
    if (!user || !session) return;
    const rankings = gameState.rankings ?? [];
    const finishPosition = rankings.indexOf(localPlayerId) + 1;
    if (finishPosition === 0) return;
    try {
      await fetch(`${API_URL}/api/game/result`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ finishPosition, totalPlayers: playerCount, roomId: room?.roomId ?? null }),
      });
    } catch (_) {}
  }

  function openAuthModal(mode) {
    setError("");
    setAuthError("");
    setNavOpen(false);
    setAuthModalMode(mode);
  }

  async function handleSocialAuth(provider) {
    setAuthError("");
    setAuthLoading(true);
    try {
      // 'meta' maps to 'facebook' in Supabase OAuth providers
      await signInWithOAuth(provider === "meta" ? "facebook" : provider);
      // Supabase redirects the page — modal will close on return
    } catch (err) {
      setAuthError(err.message || "Social login failed.");
    } finally {
      setAuthLoading(false);
    }
  }

  async function submitAuth(event) {
    event.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      if (authModalMode === "signup") {
        await signUp(
          authForm.email.trim(),
          authForm.password,
          authForm.name.trim() || authForm.email.split("@")[0]
        );
        setAuthError("Check your email to confirm your account.");
      } else {
        await signInWithEmail(authForm.email.trim(), authForm.password);
        playSound("finish", { volume: 0.3 });
        setAuthModalMode(null);
      }
      setAuthForm((prev) => ({ ...prev, password: "" }));
    } catch (err) {
      setAuthError(err.message || "Authentication failed.");
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut();
      setNavOpen(false);
    } catch (err) {
      setError(err.message || "Sign out failed.");
    }
  }

  function handleExitGame() {
    setShowExitConfirm(false);
    setPlayerPoints({});
    if (spectatorMode) {
      clientRef.current?.send("leave_spectate", {});
      setSpectatorMode(false);
      setSpectatorCount(0);
      setSpectatorReactions([]);
    }
    if (room) {
      clientRef.current?.send("leave_room", {});
      setRoom(null);
      setLocalPlayerId(null);
      setRemoteGameState(null);
      setRemoteAction(null);
    }
    setIsRoomPublic(false);
    setRobotMatch(false);
    setSpeedMode(false);
    setTeamMode(false);
    setAutoTurnPlayers({});
    setGameKey((key) => key + 1);
    gameResultSavedRef.current = false;
  }

  const sendRoll = useCallback((dice) => {
    clientRef.current?.send("roll_dice", { dice });
  }, []);

  const sendMove = useCallback((gotiId) => {
    clientRef.current?.send("move_goti", { gotiId });
  }, []);

  // Stable ref so syncState callback never changes identity even as deps evolve
  const syncStateDepsRef = useRef(null);
  syncStateDepsRef.current = { playerCount, localPlayerId, saveGameResult };

  const syncState = useCallback((gameState) => {
    gameVersionRef.current += 1;
    const versionedState = { ...gameState, version: gameVersionRef.current };
    const { playerCount: pc, localPlayerId: lpId, saveGameResult: sgr } = syncStateDepsRef.current;
    clientRef.current?.send("sync_state", { gameState: versionedState, playerCount: pc });
    if (!gameResultSavedRef.current && versionedState.rankings?.includes(lpId)) {
      gameResultSavedRef.current = true;
      sgr(versionedState);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTurnChange = useCallback((nextTurnInfo) => {
    setTurnInfo(nextTurnInfo);
  }, []);

  async function fetchPublicRooms() {
    setLoadingPublicRooms(true);
    try {
      const res = await fetch(`${API_URL}/api/rooms`);
      setPublicRooms(await res.json());
    } catch {
      setPublicRooms([]);
    } finally {
      setLoadingPublicRooms(false);
    }
  }

  async function handleNearbyClick() {
    setNearbyStatus("searching");
    setNearbyRoomCode("");
    setRoomModalMode("nearby");
    setError("");
    setMatchType("4");
    setMatchMode("normal");
    setTeamMode(false);
    setSpeedMode(false);
    setPlayerCount(NEARBY_PLAYER_COUNT);

    try {
      if (connectionStatus !== "connected") {
        throw new Error("Realtime connection is not ready yet.");
      }

      // Step 1: check if a nearby room already exists for this IP
      const findRes = await fetch(`${API_URL}/api/nearby/find`);
      if (!findRes.ok) throw new Error("Failed to search nearby rooms.");
      const findData = await findRes.json();

      if (findData.roomCode) {
        // Another player on same WiFi already hosted — join them
        setNearbyStatus("joining");
        setNearbyRoomCode(findData.roomCode);
        joinNearbyRoom(findData.roomCode);
        return;
      }

      // Step 2: no nearby room — host one
      const hostRes = await fetch(`${API_URL}/api/nearby/host`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerCount: NEARBY_PLAYER_COUNT }),
      });
      if (!hostRes.ok) throw new Error("Failed to create nearby room.");
      const hostData = await hostRes.json();
      if (!hostData.roomCode) throw new Error("Failed to create nearby room.");

      setNearbyStatus("hosting");
      setNearbyRoomCode(hostData.roomCode);
      joinNearbyRoom(hostData.roomCode, 0);
    } catch (err) {
      console.error("[Nearby] error:", err);
      setNearbyStatus("error");
      setError(err.message || "Could not connect. Make sure the server is running.");
    }
  }

  function joinNearbyRoom(nextRoomCode, preferredPlayerId = null) {
    const cleanRoomCode = String(nextRoomCode || "").trim().toUpperCase();
    if (!cleanRoomCode) throw new Error("Nearby room was not created.");

    setPlayerCount(NEARBY_PLAYER_COUNT);
    clientRef.current?.send("join_room", {
      roomId: cleanRoomCode,
      clientId,
      name: playerName.trim() || "Player",
      playerCount: NEARBY_PLAYER_COUNT,
      ...(Number.isInteger(preferredPlayerId) ? { preferredPlayerId } : {}),
    });
  }

  function joinSpectate(targetRoomId) {
    setError("");
    clientRef.current?.send("join_spectate", {
      roomId: targetRoomId,
      clientId,
      name: playerName.trim() || "Spectator",
    });
  }

  const sendSpectatorReaction = useCallback((emoji) => {
    clientRef.current?.send("spectator_reaction", { emoji });
  }, []);

  function toggleRoomPublic() {
    const next = !isRoomPublic;
    setIsRoomPublic(next);
    clientRef.current?.send("set_room_public", { isPublic: next });
  }

  const handlePointsChange = useCallback((pts) => {
    setPlayerPoints(pts);
  }, []);

  const toggleAutoTurn = useCallback((playerId) => {
    setAutoTurnPlayers((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
  }, []);

  const handlePlayAgain = useCallback(() => {
    setPlayerPoints({});
    setAutoTurnPlayers({});
    gameResultSavedRef.current = false;
    setGameKey((k) => k + 1);
  }, []);

  const online = Boolean(room);

  const boardPlayerNames = useMemo(() => {
    if (online && room?.players) {
      const arr = Array.from({ length: playerCount }, (_, i) => `P${i + 1}`);
      room.players.forEach((p) => { arr[p.playerId] = p.name || `P${p.playerId + 1}`; });
      return arr;
    }
    return Array.from({ length: playerCount }, (_, i) => {
      if (i === 0) return playerName || "Player";
      if (teamMode && i === 2) return `${(playerName || "Player").split(" ")[0]}'s Ally`;
      return `Bot ${i + 1}`;
    });
  }, [playerCount, playerName, teamMode, online, room]);

  return (
    <div className="App">
      <header className="appHeader">
        <button
          className="brandBlock brandMarkBtn"
          onClick={() => {
            if (room || robotMatch) setShowExitConfirm(true);
          }}
          aria-label="Ludo Mania home"
        >
          <span className="brandMark">L</span>
          <div>
            <strong>Ludo Mania</strong>
            <small>Realtime Play</small>
          </div>
        </button>

        <button
          className="menuButton"
          onClick={() => setNavOpen((open) => !open)}
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
        >
          <span />
          <span />
          <span />
        </button>

        <nav
          className={`topNav ${navOpen ? "topNavOpen" : ""}`}
          aria-label="Game navigation"
        >
          <span className={`status status-${connectionStatus}`}>
            {connectionStatus}
          </span>
          {room && !spectatorMode && (
            <span className="roomBadge">
              Room <b>{room.roomId}</b> | P{localPlayerId + 1}
              <button
                className="inviteBtn"
                title="Copy invite link"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.search = `?room=${room.roomId}`;
                  navigator.clipboard.writeText(url.toString()).catch(() => {});
                }}
              >
                🔗
              </button>
              {localPlayerId === 0 && (
                <button
                  className={`makePublicBtn ${isRoomPublic ? "makePublicBtnOn" : ""}`}
                  title={isRoomPublic ? "Room is public — click to make private" : "Make room public for spectators"}
                  onClick={toggleRoomPublic}
                >
                  {isRoomPublic ? "👁️ Public" : "👁️ Private"}
                </button>
              )}
              {spectatorCount > 0 && (
                <span className="spectatorCountNav">👀 {spectatorCount}</span>
              )}
            </span>
          )}
          {room && spectatorMode && (
            <span className="roomBadge spectatingBadge">
              👁️ Spectating <b>{room.roomId}</b>
              {spectatorCount > 0 && <span className="spectatorCountNav">👀 {spectatorCount}</span>}
            </span>
          )}
          {robotMatch && (
            <span className="roomBadge">
              {teamMode ? "👥 Team Match" : speedMode ? "⚡ Speed Match" : "Robot Match"}
              {teamMode ? " | P1+P3 vs P2+P4" : ` | P1 vs ${playerCount - 1} bots`}
            </span>
          )}
          <button
            className="navButton navButtonPrimary"
            onClick={() => openRoomModal("join")}
            disabled={online}
          >
            Rooms
          </button>
          <button className="navButton" onClick={() => { setShowLeaderboard(true); setNavOpen(false); }}>
            Leaderboard
          </button>
          {user ? (
            <span className="userBadge">
              {user.avatarUrl && (
                <img src={user.avatarUrl} alt="" className="userAvatar" referrerPolicy="no-referrer" />
              )}
              {user.name}
              <button className="signOutBtn" onClick={handleSignOut} title="Sign out">
                ↩
              </button>
            </span>
          ) : (
            <button
              className="navButton"
              onClick={() => openAuthModal("login")}
              disabled={sessionLoading}
            >
              {sessionLoading ? "..." : "Account"}
            </button>
          )}
        </nav>
      </header>

      <main className="gameStage">
        {!room && !robotMatch && (
          <LudoManiaHero
            onPlayClick={() => openRoomModal("robot")}
            onRoomClick={() => openRoomModal("create")}
            onPlayWithBots={startRobotMatchWithCount}
            onWatchClick={() => { setRoomModalMode("watch"); fetchPublicRooms(); }}
            onNearbyClick={handleNearbyClick}
          />
        )}
        {(room || robotMatch) && (
          <button
            className="exitGameBtn"
            onClick={() => setShowExitConfirm(true)}
            aria-label="Exit game"
          >
            ✕ Exit
          </button>
        )}
        {(room || robotMatch) && (
          <div className="playersDock">
            {room
              ? room.players?.map((player) => {
                  const isActive = player.playerId === turnInfo.currentPlayer;
                  const color = turnInfo.colors[player.playerId] || "#888";
                  const isHuman = player.playerId === localPlayerId;
                  const isAutoOn = autoTurnPlayers[localPlayerId];
                  return (
                    <div
                      key={player.id}
                      className={`pdCard${isActive ? " pdCardActive" : ""}`}
                      style={isActive ? { "--player-color": color } : undefined}
                    >
                      <span className="pdName">
                        <span className="pdDot" style={{ background: color }} />
                        P{player.playerId + 1}: {player.name}
                      </span>
                      <span className="pdScore">{playerPoints[player.playerId] ?? 0}</span>
                      {isHuman && (
                        <button
                          className={`pdAuto${isAutoOn ? " autoTurnBtnOn" : ""}`}
                          onClick={() => toggleAutoTurn(localPlayerId)}
                          title={isAutoOn ? "Auto: ON — click to disable" : "Auto: OFF — click to enable"}
                        >
                          {isAutoOn ? "ON" : "Auto"}
                        </button>
                      )}
                    </div>
                  );
                })
              : teamMode
                ? (
                  <>
                    <div className="pdTeamLabel pdTeamLabelA">⚔ Team A</div>
                    {[0, 2].map((index) => {
                      const isActive = index === turnInfo.currentPlayer;
                      const color = turnInfo.colors[index] || "#888";
                      const isAutoOn = autoTurnPlayers[0];
                      return (
                        <div
                          key={index}
                          className={`pdCard${isActive ? " pdCardActive" : ""}`}
                          style={isActive ? { "--player-color": color } : undefined}
                        >
                          <span className="pdName">
                            <span className="pdDot" style={{ background: color }} />
                            P{index + 1}: {index === 0 ? playerName : "Bot"}
                          </span>
                          <span className="pdScore">{playerPoints[index] ?? 0}</span>
                          {index === 0 && (
                            <button
                              className={`pdAuto${isAutoOn ? " autoTurnBtnOn" : ""}`}
                              onClick={() => toggleAutoTurn(0)}
                              title={isAutoOn ? "Auto: ON — click to disable" : "Auto: OFF — click to enable"}
                            >
                              {isAutoOn ? "ON" : "Auto"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div className="pdTeamLabel pdTeamLabelB">⚔ Team B</div>
                    {[1, 3].map((index) => {
                      const isActive = index === turnInfo.currentPlayer;
                      const color = turnInfo.colors[index] || "#888";
                      return (
                        <div
                          key={index}
                          className={`pdCard${isActive ? " pdCardActive" : ""}`}
                          style={isActive ? { "--player-color": color } : undefined}
                        >
                          <span className="pdName">
                            <span className="pdDot" style={{ background: color }} />
                            P{index + 1}: Bot
                          </span>
                          <span className="pdScore">{playerPoints[index] ?? 0}</span>
                        </div>
                      );
                    })}
                  </>
                )
                : Array.from({ length: playerCount }, (_, index) => {
                  const isActive = index === turnInfo.currentPlayer;
                  const color = turnInfo.colors[index] || "#888";
                  const isAutoOn = autoTurnPlayers[0];
                  return (
                    <div
                      key={index}
                      className={`pdCard${isActive ? " pdCardActive" : ""}`}
                      style={isActive ? { "--player-color": color } : undefined}
                    >
                      <span className="pdName">
                        <span className="pdDot" style={{ background: color }} />
                        P{index + 1}: {index === 0 ? playerName : "Robot"}
                      </span>
                      <span className="pdScore">{playerPoints[index] ?? 0}</span>
                      {index === 0 && (
                        <button
                          className={`pdAuto${isAutoOn ? " autoTurnBtnOn" : ""}`}
                          onClick={() => toggleAutoTurn(0)}
                          title={isAutoOn ? "Auto: ON — click to disable" : "Auto: OFF — click to enable"}
                        >
                          {isAutoOn ? "ON" : "Auto"}
                        </button>
                      )}
                    </div>
                  );
                })}
          </div>
        )}
        {(room || robotMatch) && (
          <LudoBoard
            key={`${playerCount}-${room?.roomId || "local"}-${gameKey}`}
            playerCount={playerCount}
            SVG_SIZE={svgSize}
            online={online}
            enableBots={robotMatch}
            localPlayerId={robotMatch ? 0 : localPlayerId}
            remoteGameState={remoteGameState}
            remoteAction={remoteAction}
            onRollDice={sendRoll}
            onMoveGoti={sendMove}
            onGameStateChange={syncState}
            onTurnChange={handleTurnChange}
            onPointsChange={handlePointsChange}
            speedMode={speedMode}
            teamMode={teamMode}
            playerNames={boardPlayerNames}
            spectatorMode={spectatorMode}
            spectatorCount={spectatorCount}
            spectatorReactions={spectatorReactions}
            onSpectatorReaction={sendSpectatorReaction}
            autoTurnEnabled={autoTurnPlayers}
            onPlayAgain={!online ? handlePlayAgain : undefined}
            onExitHome={handleExitGame}
          />
        )}

      </main>

      {roomModalMode && (
        <div className="modalOverlay" role="presentation">
          <section className="roomModal" role="dialog" aria-modal="true">
            <button
              className="modalClose"
              onClick={() => setRoomModalMode(null)}
              aria-label="Close dialog"
            >
              x
            </button>

            <div className="modalHeader">
              <span className="modalEyebrow">
                {roomModalMode === "watch" ? "Live games" : roomModalMode === "nearby" ? "Same network" : "Game room"}
              </span>
              <h2>
                {roomModalMode === "create" ? "Create a room"
                  : roomModalMode === "robot" ? "Play with AI"
                  : roomModalMode === "watch" ? "👀 Watch Live"
                  : roomModalMode === "nearby" ? "📡 Play Nearby"
                  : "Join a room"}
              </h2>
              <p>
                {roomModalMode === "create"
                  ? "Set up a room and invite friends with the generated code."
                  : roomModalMode === "robot"
                    ? "Pick your match type, then jump in — bots fill the rest."
                    : roomModalMode === "watch"
                      ? "Spectate live public games. Send reactions — everyone sees them! 🔥"
                      : roomModalMode === "nearby"
                        ? "Everyone on the same WiFi auto-connects to the same room."
                        : "Enter your name and the room code shared by the host."}
              </p>
            </div>

            {/* Tabs — only for Join / Create / AI */}
            {roomModalMode !== "watch" && roomModalMode !== "nearby" && (
              <div className="authTabs" role="tablist" aria-label="Room mode">
                <button
                  className={roomModalMode === "join" ? "active" : ""}
                  onClick={() => setRoomModalMode("join")}
                  type="button"
                >
                  Join
                </button>
                <button
                  className={roomModalMode === "create" ? "active" : ""}
                  onClick={() => setRoomModalMode("create")}
                  type="button"
                >
                  Create
                </button>
                <button
                  className={roomModalMode === "robot" ? "active" : ""}
                  onClick={() => setRoomModalMode("robot")}
                  type="button"
                >
                  AI
                </button>
              </div>
            )}

            <div className="modalFields">
              {roomModalMode === "nearby" ? (
                /* ── Play Nearby ── */
                <div className="nearbyPanel">
                  <div className={`nearbyIcon ${nearbyStatus === "searching" ? "nearbyPulsing" : ""}`}>📡</div>
                  {nearbyStatus === "searching" && (
                    <>
                      <p className="nearbyTitle">Scanning your network…</p>
                      <p className="nearbySubtitle">Looking for players on the same WiFi</p>
                    </>
                  )}
                  {nearbyStatus === "joining" && (
                    <>
                      <p className="nearbyTitle">Found a nearby game!</p>
                      <div className="nearbyCode">{nearbyRoomCode}</div>
                      <p className="nearbySubtitle">Joining automatically…</p>
                    </>
                  )}
                  {nearbyStatus === "hosting" && (
                    <>
                      <p className="nearbyTitle">Your nearby room is ready</p>
                      <div className="nearbyCode">{nearbyRoomCode}</div>
                      <p className="nearbySubtitle">Joining automatically...</p>
                    </>
                  )}
                  {nearbyStatus === "idle" && (
                    <p className="nearbySubtitle">Connecting…</p>
                  )}
                  {nearbyStatus === "error" && (
                    <>
                      <p className="nearbyTitle" style={{ color: "#f87171" }}>Connection failed</p>
                      <p className="nearbySubtitle">{error || "Make sure the server is running."}</p>
                      <button className="nearbyEnterBtn" onClick={handleNearbyClick}>
                        Try again
                      </button>
                    </>
                  )}
                </div>
              ) : roomModalMode === "watch" ? (
                /* ── Watch Live list ── */
                <div className="watchLiveList">
                  <div className="watchLiveHeader">
                    <span>Live public games</span>
                    <button className="watchRefreshBtn" onClick={fetchPublicRooms} disabled={loadingPublicRooms}>
                      {loadingPublicRooms ? "⟳" : "↺ Refresh"}
                    </button>
                  </div>
                  {loadingPublicRooms && <p className="watchLiveEmpty">Loading…</p>}
                  {!loadingPublicRooms && publicRooms.length === 0 && (
                    <p className="watchLiveEmpty">No public games right now. Ask a host to make their room public!</p>
                  )}
                  {publicRooms.map((r) => (
                    <div key={r.roomId} className="watchLiveRow">
                      <div className="watchLiveInfo">
                        <span className="watchLiveCode">{r.roomId}</span>
                        <span className="watchLiveMeta">
                          {r.occupancy}/{r.playerCount} players
                          {r.spectatorCount > 0 && <> · 👀 {r.spectatorCount}</>}
                          {r.inProgress && <span className="watchLiveLive"> LIVE</span>}
                        </span>
                      </div>
                      <button
                        className="watchLiveJoinBtn"
                        onClick={() => joinSpectate(r.roomId)}
                        disabled={connectionStatus !== "connected"}
                      >
                        Watch
                      </button>
                    </div>
                  ))}
                </div>
              ) : roomModalMode === "join" ? (
                /* ── Join ── */
                <>
                  <label>
                    Your name
                    <input
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Display name"
                    />
                  </label>
                  <label>
                    Room code
                    <input
                      value={roomCode}
                      onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                    />
                  </label>
                </>
              ) : (
                /* ── Create / AI (same fields) ── */
                <>
                  <label>
                    Your name
                    <input
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Display name"
                    />
                  </label>
                  <label>
                    Match type
                    <select value={matchType} onChange={(e) => setMatchType(e.target.value)}>
                      <option value="team">👥 2v2 Team</option>
                      <option value="4">4 Player</option>
                      <option value="6">6 Player</option>
                      <option value="8">8 Player</option>
                    </select>
                  </label>
                  <div className="modeToggleRow">
                    <span className="modeToggleLabel">Mode</span>
                    <div className="modeToggleGroup">
                      <button
                        type="button"
                        className={`modeToggleBtn${matchMode === "normal" ? " modeToggleBtnActive" : ""}`}
                        onClick={() => setMatchMode("normal")}
                      >
                        🎲 Normal
                      </button>
                      <button
                        type="button"
                        className={`modeToggleBtn${matchMode === "speed" ? " modeToggleBtnActiveSpeed" : ""}`}
                        onClick={() => setMatchMode("speed")}
                      >
                        ⚡ Speed
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {error && <p className="error">{error}</p>}

            <div className="modalActions">
              <button className="navButton" onClick={() => setRoomModalMode(null)}>
                {roomModalMode === "watch" ? "Close" : "Cancel"}
              </button>
              {roomModalMode !== "watch" && roomModalMode !== "nearby" && (
                <button
                  className="navButton navButtonPrimary"
                  onClick={
                    roomModalMode === "create" ? handleCreateRoom
                      : roomModalMode === "robot" ? handleStartAI
                      : () => joinRoom()
                  }
                  disabled={roomModalMode !== "robot" && connectionStatus !== "connected"}
                >
                  {roomModalMode === "create" ? "Create Room"
                    : roomModalMode === "robot" ? "Play with AI"
                    : "Join Room"}
                </button>
              )}
            </div>
          </section>
        </div>
      )}

      {showLeaderboard && (
        <LeaderboardModal onClose={() => setShowLeaderboard(false)} />
      )}

      {showExitConfirm && (
        <div className="modalOverlay" role="presentation">
          <section className="exitConfirmCard" role="dialog" aria-modal="true" aria-labelledby="exitConfirmTitle">
            <p id="exitConfirmTitle" className="exitConfirmMsg">Are you sure you want to leave the game?</p>
            <div className="exitConfirmActions">
              <button
                className="navButton"
                onClick={() => setShowExitConfirm(false)}
                autoFocus
              >
                No, stay
              </button>
              <button
                className="navButton navButtonDanger"
                onClick={handleExitGame}
              >
                Yes, exit
              </button>
            </div>
          </section>
        </div>
      )}

      {authModalMode && (
        <div className="modalOverlay" role="presentation">
          <section className="roomModal authModal" role="dialog" aria-modal="true">
            <button
              className="modalClose"
              onClick={() => setAuthModalMode(null)}
              aria-label="Close auth dialog"
            >
              x
            </button>

            <div className="modalHeader">
              <span className="modalEyebrow">Player account</span>
              <h2>{authModalMode === "login" ? "Login" : "Sign up"}</h2>
              <p>Use a player profile for rooms and future leaderboard stats.</p>
            </div>

            <div className="authTabs" role="tablist" aria-label="Account mode">
              <button
                className={authModalMode === "login" ? "active" : ""}
                onClick={() => setAuthModalMode("login")}
                type="button"
              >
                Login
              </button>
              <button
                className={authModalMode === "signup" ? "active" : ""}
                onClick={() => setAuthModalMode("signup")}
                type="button"
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={submitAuth}>
              <div className="modalFields">
                {authModalMode === "signup" && (
                  <label>
                    Display name
                    <input
                      value={authForm.name}
                      onChange={(e) => setAuthForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </label>
                )}
                <label>
                  Email
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="you@example.com"
                    required
                  />
                </label>
                <label>
                  Password
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Password"
                    required
                    minLength={6}
                  />
                </label>
              </div>

              {authError && <p className="error">{authError}</p>}

              <div className="dividerText">or continue with</div>

              <div className="socialAuth">
                <button
                  type="button"
                  onClick={() => handleSocialAuth("google")}
                  disabled={authLoading}
                >
                  <span>G</span>
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialAuth("meta")}
                  disabled={authLoading}
                >
                  <span>f</span>
                  Continue with Meta
                </button>
              </div>

              <div className="modalActions">
                <button
                  type="button"
                  className="navButton"
                  onClick={() => setAuthModalMode(null)}
                  disabled={authLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="navButton navButtonPrimary"
                  disabled={authLoading}
                >
                  {authLoading
                    ? "Please wait..."
                    : authModalMode === "login"
                      ? "Login"
                      : "Create Account"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default App;
